import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { useMe } from '../../_shared/hooks/useMe';
import { useForbiddenWords } from '../hooks/useForbiddenWords';
import { useUpsertForbiddenWord } from '../hooks/useUpsertForbiddenWord';
import { useDeleteForbiddenWord } from '../hooks/useDeleteForbiddenWord';
import { ForbiddenWordFormDialog } from '../components/ForbiddenWordFormDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { SimilarityBadge } from '../components/SimilarityBadge';
import { TerminologyDownloadMenu } from '../components/TerminologyDownloadMenu';
import { TerminologyToast, useToast } from '../components/TerminologyToast';
import type { ForbiddenWord, ApiError, ForbiddenWordSearchParams } from '../types/terminology';
import type { ForbiddenWordFormValues } from '../schema/terminologySchema';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function SkeletonRows({ count = 20 }: { count?: number }) {
  return (
    <div aria-busy="true" aria-label="로딩 중">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            height: 44,
            margin: 'var(--spacing-1) 0',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-sm)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

export function ForbiddenWordListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const meQuery = useMe();
  const isAdmin = meQuery.data?.role === 'ROLE_ADMIN';

  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  useEffect(() => {
    const id = setTimeout(() => { setAppliedKeyword(keyword.trim()); setPage(0); }, 300);
    return () => clearTimeout(id);
  }, [keyword]);

  const params: ForbiddenWordSearchParams = {
    ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
    includeDeprecated,
    page,
    size,
  };

  const listQuery = useForbiddenWords(params);
  const upsertMutation = useUpsertForbiddenWord();
  const deleteMutation = useDeleteForbiddenWord();
  const { messages, addToast, dismissToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<ForbiddenWord | null>(null);
  const [formError, setFormError] = useState<string | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<ForbiddenWord | null>(null);

  const handleFormOpen = useCallback((word: ForbiddenWord | null = null) => {
    setFormError(undefined);
    setFormTarget(word);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setFormTarget(null);
    setFormError(undefined);
  }, []);

  const handleFormSubmit = useCallback(
    (values: ForbiddenWordFormValues) => {
      setFormError(undefined);
      upsertMutation.mutate(
        {
          id: formTarget?.id,
          body: {
            bad: values.bad,
            good: values.good,
            reason: values.reason || undefined,
            source: values.source || undefined,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            handleFormClose();
            addToast(
              formTarget
                ? t('terminologyManagement.toast.forbiddenUpdated')
                : t('terminologyManagement.toast.forbiddenCreated')
            );
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const msgKey = `terminologyManagement.error.${apiErr.code}`;
            const translated = t(msgKey);
            setFormError(translated !== msgKey ? translated : apiErr.message);
          },
        }
      );
    },
    [formTarget, upsertMutation, handleFormClose, addToast, t]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          addToast(t('terminologyManagement.toast.forbiddenDeprecated'));
        },
        onError: (err: unknown) => {
          setDeleteTarget(null);
          addToast((err as ApiError)?.message ?? t('terminologyManagement.error.network'), 'error');
        },
      }
    );
  }, [deleteTarget, deleteMutation, addToast, t]);

  const handleClearSearch = useCallback(() => {
    setKeyword('');
    setAppliedKeyword('');
    setIncludeDeprecated(false);
    setPage(0);
  }, []);

  const hasFilter = !!(appliedKeyword || includeDeprecated);
  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && (listQuery.data?.content?.length ?? 0) === 0;
  const hasSimilarity = (listQuery.data?.content ?? []).some(
    (w) => w.similarity !== null && w.similarity !== undefined
  );

  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 1;

  const columns: DataTableColumn<ForbiddenWord>[] = [
    {
      key: 'bad',
      header: t('terminologyManagement.forbidden.list.column.bad'),
      width: '16%',
      render: (row) => (
        <span style={{ fontWeight: 600, color: 'var(--color-danger)', fontFamily: 'var(--font-family-mono)' }}>
          {row.bad}
        </span>
      ),
    },
    {
      key: 'good',
      header: t('terminologyManagement.forbidden.list.column.good'),
      width: '18%',
      render: (row) => <span style={{ color: 'var(--color-text-primary)' }}>{row.good}</span>,
    },
    {
      key: 'reason',
      header: t('terminologyManagement.forbidden.list.column.reason'),
      width: '20%',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {row.reason ?? '-'}
        </span>
      ),
    },
    {
      key: 'source',
      header: t('terminologyManagement.forbidden.list.column.source'),
      width: '10%',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>
          {row.source}
        </span>
      ),
    },
    ...(hasSimilarity
      ? [
          {
            key: 'similarity',
            header: t('terminologyManagement.forbidden.list.column.similarity'),
            width: '8%',
            render: (row: ForbiddenWord) => <SimilarityBadge similarity={row.similarity} />,
          } as DataTableColumn<ForbiddenWord>,
        ]
      : []),
    {
      key: 'status',
      header: t('terminologyManagement.forbidden.list.column.status'),
      width: '8%',
      render: (row) => (
        <StatusPill
          tone={row.status === 'ACTIVE' ? 'success' : row.status === 'DEPRECATED' ? 'neutral' : 'warning'}
        >
          {t(`terminologyManagement.status.${row.status}`)}
        </StatusPill>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: '_actions',
            header: '',
            width: '12%',
            render: (row: ForbiddenWord) => (
              <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  aria-label={`${t('terminologyManagement.forbidden.list.rowMenu.edit')} — ${row.bad}`}
                  onClick={(e) => { e.stopPropagation(); handleFormOpen(row); }}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {t('terminologyManagement.forbidden.list.rowMenu.edit')}
                </button>
                <button
                  type="button"
                  aria-label={`${t('terminologyManagement.forbidden.list.rowMenu.deprecate')} — ${row.bad}`}
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-danger)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    color: 'var(--color-danger)',
                  }}
                >
                  {t('terminologyManagement.forbidden.list.rowMenu.deprecate')}
                </button>
              </div>
            ),
          } as DataTableColumn<ForbiddenWord>,
        ]
      : []),
  ];

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
  };

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        title={t('terminologyManagement.forbidden.list.title')}
        breadcrumb={<span>{t('terminologyManagement.forbidden.list.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <TerminologyDownloadMenu
              fixedType="forbidden"
              currentIncludeDeprecated={includeDeprecated}
              onSuccess={(msg) => addToast(msg)}
              onError={(msg) => addToast(msg, 'error')}
            />
            {isAdmin && (
              <button
                type="button"
                data-testid="forbidden-add-btn"
                onClick={() => handleFormOpen(null)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                + {t('terminologyManagement.forbidden.list.button.add')}
              </button>
            )}
          </div>
        }
      />

      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 'var(--spacing-1) 0 var(--spacing-4)' }}>
        {t('terminologyManagement.forbidden.list.subtitle', { totalForbidden: totalElements.toLocaleString() })}
      </p>

      {/* Search bar */}
      <div
        role="search"
        aria-label={t('terminologyManagement.forbidden.list.title')}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)', alignItems: 'center' }}
      >
        <label htmlFor="forbidden-search" style={{ display: 'none' }}>
          {t('terminologyManagement.forbidden.list.search.placeholder')}
        </label>
        <input
          id="forbidden-search"
          type="search"
          placeholder={t('terminologyManagement.forbidden.list.search.placeholder')}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 200px', minWidth: 180 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={includeDeprecated} onChange={(e) => { setIncludeDeprecated(e.target.checked); setPage(0); }} />
          {t('terminologyManagement.term.list.filter.includeDeprecated')}
        </label>
        {hasFilter && (
          <button type="button" onClick={handleClearSearch} style={{ ...inputStyle, cursor: 'pointer', color: 'var(--color-text-secondary)', background: 'var(--color-surface-muted)' }}>
            {t('terminologyManagement.list.empty.button.clear')}
          </button>
        )}
      </div>

      {/* 5 states */}
      {isLoading && <SkeletonRows count={size} />}

      {isError && (
        <ErrorState
          title={t('terminologyManagement.error.network')}
          message={(listQuery.error as unknown as ApiError)?.message ?? t('terminologyManagement.error.network')}
          onRetry={() => void listQuery.refetch()}
        />
      )}

      {isEmpty && (
        <EmptyState
          title={t('terminologyManagement.list.empty.title')}
          description={t('terminologyManagement.list.empty.description')}
          action={
            hasFilter ? (
              <button type="button" onClick={handleClearSearch} style={{ padding: 'var(--spacing-2) var(--spacing-4)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontFamily: 'var(--font-family-sans)' }}>
                {t('terminologyManagement.list.empty.button.clear')}
              </button>
            ) : undefined
          }
        />
      )}

      {!isLoading && !isError && !isEmpty && (
        <DataTable
          columns={columns}
          rows={listQuery.data?.content ?? []}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/admin/terminology/forbidden-words/${row.id}`)}
        />
      )}

      {/* Pagination */}
      {!isLoading && !isError && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center' }}>
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="이전 페이지" style={{ padding: 'var(--spacing-1) var(--spacing-3)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1, fontFamily: 'var(--font-family-sans)' }}>◀</button>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', padding: '0 var(--spacing-2)' }}>{page + 1} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} aria-label="다음 페이지" style={{ padding: 'var(--spacing-1) var(--spacing-3)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.5 : 1, fontFamily: 'var(--font-family-sans)' }}>▶</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <label htmlFor="fw-page-size" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>페이지당</label>
            <select id="fw-page-size" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} style={{ padding: 'var(--spacing-1) var(--spacing-2)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', background: 'var(--color-surface-elevated)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-sans)' }}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>총 {totalElements.toLocaleString(i18n.language === 'en' ? 'en-US' : 'ko-KR')}건</span>
          </div>
        </div>
      )}

      <ForbiddenWordFormDialog
        open={formOpen}
        editTarget={formTarget}
        isLoading={upsertMutation.isPending}
        errorMessage={formError}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        name={deleteTarget?.bad ?? ''}
        isLoading={deleteMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      <TerminologyToast messages={messages} onDismiss={dismissToast} />
    </div>
  );
}
